import React from 'react';

import { Badge } from '@/components/ui/badge';
import { useTokenPairStore } from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import { useLivePosition } from '../hooks/useLivePosition';
import { useMaintenanceRate } from '../hooks/useMaintenanceRate';
import type { PriceConfig } from '../hooks/usePriceConfig';
import { selectActiveTradeVisual, useTradingStore } from '../store';
import { computePnl, estimateLiquidationPrice } from '../utils/pnl';

interface PositionDetailsProps {
    currentValue: number;
    priceConfig: PriceConfig;
    compact?: boolean;
}

function formatSize(size: number | null): string {
    if (size === null || !Number.isFinite(size)) return '—';
    const abs = Math.abs(size);
    const decimals = abs >= 1000 ? 2 : abs >= 1 ? 4 : 6;
    return size.toFixed(decimals);
}

function formatUsd(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}`;
}

interface StatCellProps {
    label: string;
    value: string;
    valueClassName?: string;
    hint?: string;
}

const StatCell: React.FC<StatCellProps> = ({
    label,
    value,
    valueClassName,
    hint,
}) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {label}
            {hint && (
                <span className="ml-1 normal-case text-muted-foreground">
                    {hint}
                </span>
            )}
        </span>
        <span
            className={cn(
                'font-mono text-xs tabular-nums text-foreground/90',
                valueClassName,
            )}
        >
            {value}
        </span>
    </div>
);

/**
 * Live open-position readout. Prefers the authoritative backend values
 * (`GET /speed/position/:coin`: liquidation price, margin, effective leverage,
 * size, unrealized PnL) and falls back to the optimistic open snapshot while
 * the fill is still pending.
 */
const PositionDetails: React.FC<PositionDetailsProps> = ({
    currentValue,
    priceConfig,
    compact,
}) => {
    const live = useLivePosition();
    const activePosition = useTradingStore(selectActiveTradeVisual);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);

    const type =
        activePosition?.type ??
        (live.isLong === null ? 'LONG' : live.isLong ? 'LONG' : 'SHORT');
    const coin = live.coin ?? selectedCoin;

    const entry = live.entryPx ?? activePosition?.entryPrice ?? null;
    const leverage = live.leverage ?? activePosition?.leverage ?? null;
    const margin = live.marginUsed ?? activePosition?.amount ?? null;
    const size =
        live.size ??
        (margin !== null && leverage !== null && entry
            ? (margin * leverage) / entry
            : null);

    const maintenanceRate = useMaintenanceRate(
        margin !== null && leverage !== null ? margin * leverage : null,
    );
    const liqIsReal = live.liquidationPx !== null;
    const liq =
        live.liquidationPx ??
        (entry !== null && leverage !== null
            ? estimateLiquidationPrice(entry, type, leverage, maintenanceRate)
            : null);

    // Prefer the backend's unrealized PnL; fall back to a client estimate that
    // tracks the streaming mark price between polls.
    const pnlValue =
        live.unrealizedPnl ??
        (entry !== null && margin !== null && leverage !== null
            ? computePnl(entry, currentValue, type, margin, leverage).pnlValue
            : null);
    const pnlPositive = (pnlValue ?? 0) >= 0;

    const isLong = type === 'LONG';

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Badge
                        variant="outline"
                        className={cn(
                            'text-[10px] font-semibold',
                            isLong
                                ? 'border-success/30 bg-success/10 text-success'
                                : 'border-destructive/30 bg-destructive/10 text-destructive',
                        )}
                    >
                        {isLong ? '▲ Long' : '▼ Short'}
                    </Badge>
                    <span className="font-mono text-xs text-muted-foreground">
                        {formatSize(size)} {coin}
                    </span>
                    {leverage !== null && (
                        <Badge
                            variant="outline"
                            className="text-[10px] font-semibold text-foreground/70"
                        >
                            {leverage}×
                        </Badge>
                    )}
                </div>
                {pnlValue !== null && (
                    <span
                        className={cn(
                            'font-mono text-xs font-semibold tabular-nums',
                            pnlPositive
                                ? 'text-success'
                                : 'text-destructive',
                        )}
                    >
                        {pnlPositive ? '+' : ''}
                        {pnlValue.toFixed(2)}
                    </span>
                )}
            </div>

            <div
                className={cn(
                    'grid gap-x-3 gap-y-2 rounded-lg border border-border bg-secondary/30 p-2.5',
                    compact ? 'grid-cols-3' : 'grid-cols-2',
                )}
            >
                <StatCell
                    label="Entry"
                    value={
                        entry !== null ? `$${priceConfig.formatDisplay(entry)}` : '—'
                    }
                />
                <StatCell
                    label="Mark"
                    value={`$${priceConfig.formatDisplay(currentValue)}`}
                />
                <StatCell
                    label="Liq. price"
                    hint={liqIsReal ? undefined : 'est.'}
                    value={liq !== null ? `$${priceConfig.formatDisplay(liq)}` : '—'}
                    valueClassName="text-destructive/90"
                />
                <StatCell label="Margin" value={formatUsd(margin)} />
                {!compact && (
                    <StatCell
                        label="Size"
                        value={`${formatSize(size)} ${coin}`}
                    />
                )}
                {!compact && (
                    <StatCell
                        label="Value"
                        value={formatUsd(live.positionValue)}
                    />
                )}
            </div>
        </div>
    );
};

export default PositionDetails;
